import type {ImageField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'

const IMAGE_QUALITY = 85
const SECTION_HEIGHT_VH = 75

const getCoverSizes = (image: ImageField): string => {
  const {width, height} = image.dimensions ?? {}

  if (!width || !height) {
    return '100vw'
  }

  const widthAtFullHeight = Math.ceil((SECTION_HEIGHT_VH * width) / height)

  return `max(100vw, ${widthAtFullHeight}dvh)`
}

export type MediaQuoteUIProps = {
  backgroundImage: ImageField
  quote: string
  authorName: string
  authorTitle: string
  paddingTop?: string | null
  paddingBottom?: string | null
}

export const MediaQuote = ({
  backgroundImage,
  quote,
  authorName,
  authorTitle,
  paddingTop,
  paddingBottom,
}: MediaQuoteUIProps): JSX.Element => {
  const hasBackgroundImage = isFilled.image(backgroundImage)
  const paddingTopClass = PADDING_TOP_CLASS[paddingTop ?? DEFAULT_PADDING] ?? ''
  const paddingBottomClass = PADDING_BOTTOM_CLASS[paddingBottom ?? DEFAULT_PADDING] ?? ''

  return (
    <section
      data-slice-type="media_quote"
      data-slice-variation="default"
      data-nav-dark={hasBackgroundImage}
      className={cn('relative h-[75dvh] overflow-hidden', paddingTopClass, paddingBottomClass)}
    >
      {hasBackgroundImage && (
        <PrismicNextImage
          field={backgroundImage}
          fill
          sizes={getCoverSizes(backgroundImage)}
          imgixParams={{q: IMAGE_QUALITY}}
          className="object-cover lg:object-top"
        />
      )}
      <div className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-[#737373] to-transparent mix-blend-multiply lg:w-1/2" />
      <div className="relative z-10 flex h-full w-2/3 items-center px-5 md:px-10 lg:w-1/2 lg:pl-[max(40px,calc((100vw-1200px)/2))]">
        <div className="flex w-full max-w-[380px] flex-col gap-6 lg:max-w-[573px]">
          <svg
            width="21"
            height="11"
            viewBox="0 0 21 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M0 11V6.6C0 2.933 2.2 0.6 6.6 0L7.7 1.6C5.567 2.067 4.5 3.2 4.5 5H7.7V11H0ZM11.55 11V6.6C11.55 2.933 13.75 0.6 18.15 0L19.25 1.6C17.117 2.067 16.05 3.2 16.05 5H19.25V11H11.55Z"
              fill="white"
            />
          </svg>
          {quote && (
            <p className="font-main text-24 font-normal leading-tight text-moss-white md:text-32 lg:text-36 lg:leading-[38px]">
              {quote}
            </p>
          )}
          <div className="flex flex-col">
            {authorName && (
              <p className="font-main text-13 font-semibold uppercase leading-5 tracking-[0.78px] text-moss-white">
                {authorName}
              </p>
            )}
            {authorTitle && (
              <p className="font-main text-13 font-normal uppercase leading-5 tracking-[0.78px] text-moss-white">
                {authorTitle}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
